/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { EditableText } from '../Editable.js';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

export function Stakeholders() {
  const utils = trpc.useUtils();
  const list = trpc.stakeholders.list.useQuery();
  const create = trpc.stakeholders.create.useMutation({
    onSuccess: async () => {
      await utils.stakeholders.list.invalidate();
    },
  });
  const remove = trpc.stakeholders.remove.useMutation({
    onSuccess: async () => {
      await utils.stakeholders.list.invalidate();
    },
  });
  const update = trpc.stakeholders.update.useMutation({
    onSuccess: async () => {
      await utils.stakeholders.list.invalidate();
    },
  });

  const [draftName, setDraftName] = useState('');
  const [draftRole, setDraftRole] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const name = draftName.trim();
    if (!name) return;
    const role = draftRole.trim();
    create.mutate(
      { name, role: role === '' ? null : role, priorities: null, relationshipState: null },
      {
        onSuccess: () => {
          setDraftName('');
          setDraftRole('');
        },
      },
    );
  };

  const items = list.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>Stakeholders · key people</div>

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : items.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          No stakeholders on file.
        </p>
      ) : (
        <div>
          {items.map((s) => (
            <StakeholderRow
              key={s.id}
              name={s.name}
              role={s.role}
              priorities={s.priorities}
              relationshipState={s.relationshipState}
              onRemove={() => remove.mutate({ stakeholderId: s.id })}
              onEditName={(next) => update.mutate({ stakeholderId: s.id, name: next })}
              onEditRole={(next) =>
                update.mutate({ stakeholderId: s.id, role: next === '' ? null : next })
              }
              onEditPriorities={(next) =>
                update.mutate({ stakeholderId: s.id, priorities: next === '' ? null : next })
              }
              onEditRelationship={(next) =>
                update.mutate({ stakeholderId: s.id, relationshipState: next === '' ? null : next })
              }
              pending={remove.isPending || update.isPending}
            />
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Name"
            aria-label="Stakeholder name"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={create.isPending || draftName.trim() === ''}
            aria-label="Add stakeholder"
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: create.isPending || draftName.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: create.isPending || draftName.trim() === '' ? 0.6 : 1,
            }}
          >
            {create.isPending ? 'Adding…' : 'Add →'}
          </button>
        </div>
        <input
          type="text"
          value={draftRole}
          onChange={(e) => setDraftRole(e.target.value)}
          placeholder="Role (optional) — e.g. manager, peer, report"
          aria-label="Stakeholder role"
          style={{ ...inputStyle, fontStyle: 'italic' }}
        />
      </form>
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
    </section>
  );
}

function StakeholderRow({
  name,
  role,
  priorities,
  relationshipState,
  onRemove,
  onEditName,
  onEditRole,
  onEditPriorities,
  onEditRelationship,
  pending,
}: {
  name: string;
  role: string | null;
  priorities: string | null;
  relationshipState: string | null;
  onRemove: () => void;
  onEditName: (next: string) => void;
  onEditRole: (next: string) => void;
  onEditPriorities: (next: string) => void;
  onEditRelationship: (next: string) => void;
  pending: boolean;
}) {
  const detailStyle: React.CSSProperties = {
    fontFamily: 'var(--font-serif)',
    fontFeatureSettings: 'var(--serif-feat)',
    fontStyle: 'italic',
    fontSize: 14,
    color: p.muted,
  };
  const detailInput: React.CSSProperties = {
    ...inputStyle,
    fontSize: 14,
    padding: '6px 8px',
    fontStyle: 'italic',
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'start',
        columnGap: 12,
        rowGap: 4,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <EditableText
          value={name}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === '' || trimmed === name) return;
            onEditName(trimmed);
          }}
          ariaLabel="Edit stakeholder name"
          renderStyle={{ fontSize: 15, color: p.fg, fontWeight: 500 }}
          inputStyle={{ ...inputStyle, fontSize: 15, padding: '6px 8px' }}
        />
        <EditableText
          value={role ?? ''}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === (role ?? '')) return;
            onEditRole(trimmed);
          }}
          ariaLabel="Edit stakeholder role"
          renderStyle={detailStyle}
          inputStyle={detailInput}
          placeholder="Add a role"
        />
        <EditableText
          value={priorities ?? ''}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === (priorities ?? '')) return;
            onEditPriorities(trimmed);
          }}
          ariaLabel="Edit stakeholder priorities"
          renderStyle={detailStyle}
          inputStyle={detailInput}
          placeholder="Add their priorities"
        />
        <EditableText
          value={relationshipState ?? ''}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === (relationshipState ?? '')) return;
            onEditRelationship(trimmed);
          }}
          ariaLabel="Edit stakeholder relationship state"
          renderStyle={detailStyle}
          inputStyle={detailInput}
          placeholder="Add the relationship state"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={pending}
        title="Remove"
        aria-label="Remove stakeholder"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
          marginTop: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}
